import * as dayjs from 'dayjs';
import { PhoneNumberUtil, PhoneNumberFormat, PhoneNumberType } from 'google-libphonenumber';

import { Country } from '@shared/models/Account';
import { GraphFilterSource, GraphFilterTimePeriod, GraphFilter, REPORTING_PERFORMANCE_FILTER } from '@shared/models/Reporting';

export function changeCountryOrder(countryList: Country[], countryCode: string, secondCountryCode: string = null): Country[] {
  const list = [...countryList];

  for (let i = 0; i < list.length; i++) {
    if (list[i].ISO_CODE_2 === countryCode) {
      list.unshift(list.splice(i, 1)[0]);
    }
  }

  if (!secondCountryCode) {
    return list;
  }

  for (let t = 0; t < list.length; t++) {
    if (list[t].ISO_CODE_2 === secondCountryCode) {
      list.splice(1, 0, list.splice(t, 1)[0]);
    }
  }

  return list;
}

export function ageCheck(birthdate: string[]): boolean {
  const date = new Date(birthdate.join('/'));

  if (date.getFullYear() >= new Date().getFullYear()) {
    return true;
  }

  const ageDifMs = Date.now() - date.getTime();
  const ageDate = new Date(ageDifMs);
  const finalAge = Math.abs(ageDate.getUTCFullYear() - 1970);

  if (finalAge < 18) {
    return true;
  }

  return false;
}

export function isValidDate(birthdate: string[]): boolean {
  const actualYear = parseInt(birthdate[0], 10);
  const actualMonth = parseInt(birthdate[1], 10) - 1;
  const actualDay = parseInt(birthdate[2], 10);
  const date = new Date(birthdate.join('/'));

  if (date.getFullYear() === actualYear && date.getMonth() === actualMonth && date.getDate() === actualDay) {
    return true;
  }

  return false;
}

export function allowNumericInputOnly(event: KeyboardEvent): boolean {
  const onlyNumbers = /^([0-9])$/;
  return onlyNumbers.test(event.key);
}

export function downloadExternalFile(file: Blob | MediaSource, fileName: string): void {
  const downloadLink = document.createElement('a');
  downloadLink.style.display = 'none';
  document.body.appendChild(downloadLink);
  downloadLink.setAttribute('href', window.URL.createObjectURL(file));
  downloadLink.setAttribute('target', '_blank');
  downloadLink.setAttribute('download', fileName);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

export function validateZipCode(zip: string): Promise<any> {
  // TOOD: Update token interceptor method on http get request and start to use NGRX
  // Lovely vanillaJS - because of CORS
  return fetch(
    `https://swisspost.opendatasoft.com/api/records/1.0/search/?dataset=plz_verzeichnis_v2&q=&facet=postleitzahl&facet=ortbez18&refine.postleitzahl=${zip}`
  ).then((response) => response.json());
}

function getTodayString(): string {
  return dayjs().format('MM/DD/YYYY');
}

export function isHolidays(holidayList: string[]): boolean {
  const today = new Date();
  const todayIs = getTodayString();
  let isPublicHoliday = false;
  const day = today.getDay();
  const isSunday = day === 0; // if it's Sunday

  isPublicHoliday = holidayList.indexOf(todayIs) > -1;

  return isSunday || isPublicHoliday;
}

export function checkIfOnline(bankHolidays: string[]): boolean {
  const today = new Date();
  const todayIs = getTodayString();

  if (bankHolidays.indexOf(todayIs) > -1) {
    return today.getHours() >= 7 && today.getHours() < 16;
  }
  return today.getHours() >= 7 && today.getHours() < 22;
}

export function anonymiseEmail(email: string): string {
  return btoa(email.split('@')[0]);
}

export function anonymiseRedirectURL(url: string): string {
  return btoa(url);
}

export function decodeRedirectURL(url: string): string {
  return atob(url);
}

export const publicURLs = [
  '/login',
  '/reset-password',
  '/create-profile',
  '/profil-erstellen',
  '/creer-profil',
  '/invite:code',
  '/verify/success',
  '/verify/expired',
  '/verify/invalid'
];

/**
 * @description Round provided date time to the closest half value
 * @example 18:25 -> 18:30, 18:32 -> 19:00
 * @param date {Date}
 * @return date {Date}
 */
export const roundTheTime30Min = (date: Date): Date => {
  const time = 1000 * 60 * 30;
  const roundedDate = new Date(Math.ceil(date.getTime() / time) * time);
  roundedDate.setSeconds(0, 0);
  return roundedDate;
};

/**
 * @description Convert Date object to string with date and time without zone information
 * @example 2022-01-10T08:30
 * @param date {Date}
 * @return date/time {string}
 */
export const getFormattedDate = (date: Date): string => {
  return `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1}-${date.getDate()}T${
    date.getHours() < 10 ? '0' : ''
  }${date.getHours()}:${date.getMinutes() < 10 ? '0' : ''}${date.getMinutes()}`;
};

/**
 * @description Check if the provided Date expired and return new rounded date if it is
 * @param date { Date }
 * @return provided or current rounded Date { Date }
 */
export const checkIfDateExpired = (date: Date): Date => {
  const currentDate = roundTheTime30Min(new Date());
  return currentDate.getTime() > date.getTime() ? currentDate : date;
};

export function getDocumentExtFromUrl(url: string): string {
  const split = url.split('.');
  return split[split.length - 1].toUpperCase();
}

/**
 * Parse a localized number to a float.
 * @param {string} stringNumber - the localized number
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 * https://stackoverflow.com/questions/29255843/is-there-a-way-to-reverse-the-formatting-by-intl-numberformat-in-javascript
 */
export function parseLocaleNumber(stringNumber: string, locale?: string | string[]) {
  const thousandSeparator = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, '');
  const decimalSeparator = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, '');

  return parseFloat(stringNumber.replace(new RegExp('\\' + thousandSeparator, 'g'), '').replace(new RegExp('\\' + decimalSeparator), '.'));
}

/**
 * @description Converts date string from "1926/9/21" to "1926-09-21" format
 * @param inputDate {string}
 */
export function mapDateString(inputDate: string): string {
  const dateComponents = inputDate.split('/');

  const year = dateComponents[0];
  const month = dateComponents[1].padStart(2, '0'); // Ensure 2-digit month
  const day = dateComponents[2].padStart(2, '0'); // Ensure 2-digit day
  return `${year}-${month}-${day}`;
}

export function validateAHV(ahv: string) {
  const regex = /[7][5][6][\d]{10}$/;
  let isValid = false;
  isValid = regex.test(ahv);

  if (!isValid) {
    return false;
  }

  let tmpAHV = ahv.slice(0, -1); // remove last character as it's not needed for sum check
  tmpAHV = tmpAHV.split('').reverse().join(''); // reverse string
  let sum = 0;
  for (let i = 0; i < tmpAHV.length; i++) {
    const add = i % 2 == 0 ? parseInt(tmpAHV[i]) * 3 : parseInt(tmpAHV[i]) * 1;
    sum += add;
  }

  //check if AHV is valid
  const checksum = Math.ceil(sum / 10) * 10 - sum;
  const checknumber = parseInt(ahv[ahv.length - 1]); //get last digit
  if (checksum !== checknumber) {
    isValid = false;
  }

  return isValid;
}

export function getCookie(name: string): string {
  const ca: Array<string> = document.cookie.split(';');
  const caLen: number = ca.length;
  const cookieName = `${name}=`;
  let c: string;
  let cook: string[];

  for (let i = 0; i < caLen; i += 1) {
    c = ca[i].replace(/^\s+/g, '');
    if (c.indexOf(cookieName) === 0) {
      cook = c.substring(cookieName.length, c.length).split('&');
      // Try to keep it until to understand reasons
      // for (let j = 0; j < cook.length; j += 1) {
      //   cook[j] = decodeURIComponent(cook[j].replace('+', ' '));
      // }
      return cook.toString();
    }
  }

  return '';
}

// 0.041666667 days = 1 hours
export function setCookie(name: string, value: string, days = 0.041666667): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; Expires=${date.toUTCString()}; Path=/; Secure;`;
}

export function removeCookie(name: string): void {
  const expireDate = 'Wed, 1 Apr 2020 08:00:00 GMT';
  document.cookie = `${name}=; Expires=${expireDate}; Path=/; Secure;`;
}

export function getRedirectionDomainInfo(appLocation: string, inyovaPro: boolean): { finalDomain: string; appLocation: string } {
  const isStaging = origin.includes('staging');
  const inyovaDomain = isStaging ? 'staging' : 'app';
  const proDomain = isStaging ? 'pro.staging' : 'pro';

  const finalDomain = inyovaPro ? proDomain : inyovaDomain;

  return { finalDomain, appLocation };
}

export function getPercentageByDate(start: string, end: string): number {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  const totalTime = endDate.diff(startDate, 'hours');
  const progress = endDate.diff(dayjs(), 'hours');
  const percentage = (progress / totalTime) * 100;
  return percentage;
}

export function formatDate(date: string, seperator: '.' | '-', lang: string) {
  if (!date) {
    return '';
  }

  let finalDate = date;

  if (seperator === '.') {
    const dateSplited = date.split(seperator);
    finalDate = `${dateSplited[1]}/${dateSplited[0]}/${dateSplited[2]}`;
  }

  return new Date(finalDate).toLocaleString(lang, {
    year: 'numeric',
    day: 'numeric',
    month: 'short'
  });
}

export function extractNumberAndCountryCodeFromPhoneNumber(phoneNumber: string | null): { number: string; countryCode: string } | null {
  if (!phoneNumber) return null;
  const phoneUtil = PhoneNumberUtil.getInstance();
  const parsedNumber = phoneUtil.parse(phoneNumber);

  return {
    number: phoneUtil
      .format(parsedNumber, PhoneNumberFormat.NATIONAL)
      .replace(/^\s*0+/, '')
      .trim(),
    countryCode: phoneUtil.getRegionCodeForNumber(parsedNumber)
  };
}

export function getExampleMobilePhoneNumberForCountry(countryCode: string): string {
  const phoneUtil = PhoneNumberUtil.getInstance();
  return PhoneNumberUtil.getInstance().format(phoneUtil.getExampleNumberForType(countryCode, PhoneNumberType.MOBILE), PhoneNumberFormat.National);
}

// Amplitude
// CH 3b DE 3b
export const getInvestmentDuration = (answers: any[]) => {
  const answerId = answers.find((item) => item.question_id === 6 || item.question_id === 12)?.answer;
  switch (answerId) {
    case 9:
    case 35:
      return '0-5';
    case 10:
    case 36:
      return '5-10';
    case 11:
    case 37:
      return '10-20';
    case 12:
    case 38:
      return '20+';
  }
};

export const getAccAge = (answers: any[]) => {
  const answerId = answers.find((item) => item.question_id === 7 || item.question_id === 13)?.answer;
  switch (answerId) {
    case 13:
    case 39:
      return 24;
    case 14:
    case 40:
      return 35;
    case 15:
    case 41:
      return 45;
    case 16:
    case 42:
      return 55;
    case 17:
    case 43:
      return 60;
  }
};
export const getInvestmentPercentage = (answers: any[]) => {
  const answerId = answers.find((item) => item.question_id === 8 || item.question_id === 14)?.answer;
  switch (answerId) {
    case 22:
    case 48:
      return '0-20';
    case 21:
    case 47:
      return '20-40';
    case 20:
    case 46:
      return '40-60';
    case 19:
    case 45:
      return '60-80';
    case 18:
    case 44:
      return '80-100';
  }
};

export const getExistingInvestment = (survey: any[], questionId: number) => {
  const investmentQuestion = survey.find((item) => item.id === questionId);
  if (!investmentQuestion) {
    return null;
  }
  return investmentQuestion.survey_answer_options.find((item) => item.id === investmentQuestion.survey_customer_answer?.answer)?.weight;
};

export const decodeRedirectUrlFromQueryParam = (redirectParam: string): string | null => {
  if (!redirectParam || redirectParam.length <= 0) {
    return null;
  }
  const pathRegex = /^\/[\w\-\/?=&#%]*$/;
  const url = decodeRedirectURL(decodeURIComponent(redirectParam) ?? '');
  return pathRegex.test(url) ? url : null;
};

function getDefaultPerformanceFilter(): GraphFilter {
  return {
    timePeriod: GraphFilterTimePeriod.MAX,
    source: GraphFilterSource.PERFORMANCE
  };
}

export const getStoredPerformanceFilter = (): GraphFilter => {
  if (typeof localStorage === 'undefined') {
    return getDefaultPerformanceFilter();
  }

  const rawValue = localStorage.getItem(REPORTING_PERFORMANCE_FILTER);

  if (!rawValue) {
    return getDefaultPerformanceFilter();
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<GraphFilter>;
    const timePeriodValues = Object.values(GraphFilterTimePeriod) as string[];
    const sourceValues = Object.values(GraphFilterSource) as string[];

    if (!parsedValue || !timePeriodValues.includes(parsedValue.timePeriod) || !sourceValues.includes(parsedValue.source)) {
      return getDefaultPerformanceFilter();
    }

    return {
      source: parsedValue.source,
      timePeriod: parsedValue.timePeriod
    };
  } catch {
    return getDefaultPerformanceFilter();
  }
};
